import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import ExportButton from "../components/common/ExportButton";
import { EVENT_TYPES } from "../utils/constants";
import {
  getEventExecutiveSummaries,
  type EventExecutiveSort,
  type EventExecutiveSummaryFilter,
  type EventExecutiveSummaryResult,
  type EventExecutiveSummaryRow,
} from "../services/eventExecutiveSummary.service";
import {
  CHECKIN_COMPONENTS,
  totalCheckins,
} from "../utils/eventCheckinBreakdown";
import EventStatusBadge from "../components/events/EventStatusBadge";
import UpcomingEventsSection from "../components/events/UpcomingEventsSection";
import OpenForRegistrationSection from "../components/events/OpenForRegistrationSection";

const PAGE_SIZE = 50;

// Static US state codes so the geographic filter is complete regardless of which states
// happen to appear on the current page. Any non-standard code present in the data is merged
// in at render time (see stateOptions).
const US_STATE_CODES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

interface Filters {
  dateFrom?: string;
  dateTo?: string;
  eventType?: string;
  // false => hide completed, i.e. only events that have not reached their end
  // time yet. undefined/true => show both.
  includeCompleted?: boolean;
  // Single-select geographic filter, carried as a 1-element array for the RPC.
  states?: string[];
  sortBy: EventExecutiveSort;
  sortDirection: "asc" | "desc";
}

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const count = new Intl.NumberFormat("en-US");

const safeSpreadsheetText = (value: string): string =>
  /^[=+\-@]/.test(value) ? `'${value}` : value;

const formatDate = (value: string | null): string => {
  if (!value) {
    return "—";
  }
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return new Date(year, month - 1, day).toLocaleDateString();
};

const formatRefresh = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

// Shown as the column's tooltip. A CSV has no tooltip, so its header is spelled out instead.
const PROMO_NEW_MEMBERS_HELP =
  "New and returning members whose membership was bought with the event organization's promo code " +
  "(the promoter for a gun show, the host for a range night) between the event's opening time and " +
  "midnight of its last day, event local time. Renewals and upgrades of a continuing membership are " +
  "not counted. If a later event by the same organization has opened, the sign-up goes to the later one. " +
  "When the organization's events overlap, a member counts for the event where they used a voucher or " +
  "checked in; otherwise for neither. Counts events with no vouchers too (insurance states).";

const EventPerformance: React.FC = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    sortBy: "event_local_date",
    sortDirection: "desc",
  });

  const rpcFilter = useMemo<EventExecutiveSummaryFilter>(
    () => ({ ...filters, page, limit: PAGE_SIZE }),
    [filters, page],
  );

  const { data, isLoading, error } = useQuery<EventExecutiveSummaryResult>({
    queryKey: ["event-executive-summary", rpcFilter],
    queryFn: () => getEventExecutiveSummaries(rpcFilter),
    staleTime: 60_000,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const stateOptions = useMemo(() => {
    const codes = new Set<string>(US_STATE_CODES);
    for (const row of rows) {
      if (row.event_state) {
        codes.add(row.event_state);
      }
    }
    return Array.from(codes).sort();
  }, [rows]);

  const selectedState = filters.states?.[0] ?? "";
  const hideCompleted = filters.includeCompleted === false;
  const hasActiveFilters =
    Boolean(filters.dateFrom) ||
    Boolean(filters.dateTo) ||
    Boolean(filters.eventType) ||
    hideCompleted ||
    Boolean(selectedState);

  const updateFilter = (patch: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const toggleSort = (sortBy: EventExecutiveSort) => {
    updateFilter({
      sortBy,
      sortDirection:
        filters.sortBy === sortBy && filters.sortDirection === "desc"
          ? "asc"
          : "desc",
    });
  };

  const sortLabel = (label: string, key: EventExecutiveSort) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 hover:text-gray-900"
      onClick={() => toggleSort(key)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      {filters.sortBy === key && (
        <span aria-hidden="true">
          {filters.sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
    </button>
  );

  const exportColumns = [
    "Event ID",
    "Event Date",
    "Event Timezone",
    "Event Name",
    "Event Type",
    "Event Status",
    "Event State",
    "Event Location",
    "Salespeople",
    "Operators",
    "Unique Invited People",
    "Initial Invitation Messages",
    "Reminder Messages",
    "Total Campaign Messages",
    "Member Registrations",
    "Guest Registrations",
    ...CHECKIN_COMPONENTS.map((c) => c.csvLabel),
    "Total Check-ins",
    "New Members (Promo Code)",
    "Subscription Vouchers Minted",
    "Total Vouchers Minted",
    "Total Voucher Value Minted",
    "Vouchers Redeemed",
    "Redeemed Voucher Value",
    "Last Refreshed",
    "Definition Version",
  ];

  const mapExportRows = (
    exportRows: EventExecutiveSummaryRow[],
  ): Record<string, unknown>[] =>
    exportRows.map((row) => ({
      "Event ID": row.event_id,
      "Event Date": row.event_local_date || "",
      "Event Timezone": safeSpreadsheetText(row.event_timezone || ""),
      "Event Name": safeSpreadsheetText(row.event_title),
      "Event Type": safeSpreadsheetText(row.event_type),
      "Event Status": safeSpreadsheetText(row.event_status),
      "Event State": safeSpreadsheetText(row.event_state || ""),
      "Event Location": safeSpreadsheetText(row.location_display),
      Salespeople: safeSpreadsheetText(row.salespeople),
      Operators: safeSpreadsheetText(row.operators),
      // Four SEPARATE measures (spec item 1). People and messages are never the same number, and
      // a message count is never labelled as people.
      "Unique Invited People": row.invited_people_count,
      "Initial Invitation Messages": row.campaign_invites_sent_count,
      "Reminder Messages": row.reminder_messages_count,
      "Total Campaign Messages": row.total_campaign_messages_count,
      "Member Registrations": row.member_registrations,
      "Guest Registrations": row.guest_registrations,
      ...Object.fromEntries(
        CHECKIN_COMPONENTS.map((c) => [c.csvLabel, row[c.key]]),
      ),
      "Total Check-ins": totalCheckins(row),
      "New Members (Promo Code)": row.promo_new_members,
      "Subscription Vouchers Minted": row.subscription_vouchers_minted,
      "Total Vouchers Minted": row.total_vouchers_minted,
      "Total Voucher Value Minted": row.total_voucher_value_minted.toFixed(2),
      "Vouchers Redeemed": row.vouchers_redeemed,
      "Redeemed Voucher Value": row.redeemed_voucher_value.toFixed(2),
      "Last Refreshed": row.refreshed_at,
      "Definition Version": row.definition_version,
    }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Event Performance
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Executive event outcomes from a maintained reporting summary. An
            event appears once its own local day has arrived — including while
            it is still running — and is judged by its start and end times, not
            by the status somebody saved on it. Cancelled events are excluded;
            events that have not started yet are listed under Upcoming. Data
            normally refreshes within five minutes. Unique invited people and
            campaign messages sent are separate because one is person-grain and
            the other is message-grain.
          </p>
        </div>
        <ExportButton<Filters, EventExecutiveSummaryRow>
          filters={filters}
          fetchPage={(exportFilters, exportPage, limit) =>
            getEventExecutiveSummaries({
              ...exportFilters,
              page: exportPage,
              limit,
            })
          }
          mapRows={mapExportRows}
          columns={exportColumns}
          filename={`event-performance-${filters.dateFrom || "all"}-${filters.dateTo || "all"}`}
          format="csv"
          label="Download CSV"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <label className="text-sm text-gray-700">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            From
          </span>
          <input
            type="date"
            value={filters.dateFrom || ""}
            onChange={(event) =>
              updateFilter({ dateFrom: event.target.value || undefined })
            }
            className="rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <label className="text-sm text-gray-700">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            To
          </span>
          <input
            type="date"
            value={filters.dateTo || ""}
            onChange={(event) =>
              updateFilter({ dateTo: event.target.value || undefined })
            }
            className="rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <label className="text-sm text-gray-700">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            Event type
          </span>
          <select
            value={filters.eventType || ""}
            onChange={(event) =>
              updateFilter({ eventType: event.target.value || undefined })
            }
            className="min-w-48 rounded-md border border-gray-300 px-3 py-2"
          >
            <option value="">All event types</option>
            {EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            State
          </span>
          <select
            aria-label="Filter by state"
            value={selectedState}
            onChange={(event) =>
              updateFilter({
                states: event.target.value ? [event.target.value] : undefined,
              })
            }
            className="min-w-40 rounded-md border border-gray-300 px-3 py-2"
          >
            <option value="">All states</option>
            {stateOptions.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={(event) =>
              updateFilter({
                includeCompleted: event.target.checked ? false : true,
              })
            }
            className="h-4 w-4 rounded border-gray-300"
          />
          <span>
            Hide completed
            <span className="ml-1 text-xs text-gray-500">
              (show only events happening now)
            </span>
          </span>
        </label>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setFilters({
                sortBy: "event_local_date",
                sortDirection: "desc",
              });
              setPage(1);
            }}
            className="rounded-md px-3 py-2 text-sm text-primary-700 hover:bg-primary-50"
          >
            Clear filters
          </button>
        )}
      </div>

      <OpenForRegistrationSection
        eventType={filters.eventType}
        states={filters.states}
      />

      <UpcomingEventsSection
        eventType={filters.eventType}
        states={filters.states}
      />

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error instanceof Error
            ? error.message
            : "Unable to load Event Performance"}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* Height-capped so the horizontal scrollbar is always on screen (owner 2026-09-29: you had
            to scroll to the bottom to reach it for an event at the top). Header row sticks while
            scrolling; the first column already sticks left, and its header cell sits above both. */}
        <div className="overflow-auto max-h-[calc(100vh-26rem)] min-h-[18rem]">
          <table className="min-w-[2200px] divide-y divide-gray-200 text-sm">
            <thead className="sticky top-0 z-20 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="sticky left-0 z-30 bg-gray-50 px-4 py-3">
                  {sortLabel("Event", "event_title")}
                </th>
                <th className="px-4 py-3">
                  {sortLabel("Date", "event_local_date")}
                </th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Salespeople</th>
                <th className="px-4 py-3">Operators</th>
                <th className="px-4 py-3">Unique invited people</th>
                <th className="px-4 py-3">Initial invites (messages)</th>
                <th className="px-4 py-3">Reminders (messages)</th>
                <th className="px-4 py-3">Total messages</th>
                <th className="px-4 py-3">Member registrations</th>
                <th className="px-4 py-3">Guest registrations</th>
                {/* Every component of Total Check-ins gets a column. Previously the unclassified
                    pair was added into the total but had no column, so the visible numbers did not
                    add up to the total printed beside them. */}
                {CHECKIN_COMPONENTS.map((c) => (
                  <th key={c.key} className="px-4 py-3">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 font-semibold text-gray-800">
                  Total check-ins
                </th>
                <th className="px-4 py-3" title={PROMO_NEW_MEMBERS_HELP}>
                  {sortLabel("New members (promo code)", "promo_new_members")}
                </th>
                <th className="px-4 py-3">Subscription vouchers</th>
                <th className="px-4 py-3">
                  {sortLabel("Total vouchers", "total_vouchers_minted")}
                </th>
                <th className="px-4 py-3">Minted value</th>
                <th className="px-4 py-3">
                  {sortLabel("Redeemed", "vouchers_redeemed")}
                </th>
                <th className="px-4 py-3">Redeemed value</th>
                <th className="px-4 py-3">Last refreshed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={23}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Loading Event Performance…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={23}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No events match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.event_id} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-3 hover:bg-gray-50">
                      <Link
                        className="font-medium text-primary-700 hover:underline"
                        to={`/events/${row.event_id}`}
                      >
                        {row.event_title}
                      </Link>
                      <div className="mt-0.5">
                        {/* Shared badge: the saved status is not evidence of
                            liveness, so it is derived from the event's own
                            window (and rendered instead of the raw snake_case). */}
                        <EventStatusBadge
                          event={{
                            status: row.event_status,
                            start_time: row.event_start_at,
                            end_time: row.event_end_at,
                            timezone: row.event_timezone,
                          }}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {formatDate(row.event_local_date)}
                    </td>
                    <td className="px-4 py-3">{row.event_type}</td>
                    <td className="px-4 py-3">{row.event_state || "—"}</td>
                    <td className="max-w-56 px-4 py-3">
                      {row.location_display || "—"}
                    </td>
                    <td className="max-w-56 px-4 py-3">
                      {row.salespeople || "—"}
                    </td>
                    <td className="max-w-56 px-4 py-3">
                      {row.operators || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.invited_people_count)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.campaign_invites_sent_count)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.reminder_messages_count)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.total_campaign_messages_count)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.member_registrations)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.guest_registrations)}
                    </td>
                    {CHECKIN_COMPONENTS.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-right">
                        {count.format(row[c.key])}
                      </td>
                    ))}
                    {/* Derived from the SAME list that produced the cells above, so a row can
                        never display components that fail to add up to this number. */}
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {count.format(totalCheckins(row))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.promo_new_members)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.subscription_vouchers_minted)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.total_vouchers_minted)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {money.format(row.total_voucher_value_minted)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {count.format(row.vouchers_redeemed)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {money.format(row.redeemed_voucher_value)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                      {formatRefresh(row.refreshed_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
          <span>
            {total === 0
              ? "0 events"
              : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded border border-gray-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              className="rounded border border-gray-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {rows.some(
        (row) =>
          row.unclassified_member_checkins > 0 ||
          row.unclassified_nonmember_checkins > 0,
      ) && (
        <p className="text-xs text-gray-500">
          Historical check-ins without a durable channel remain unclassified.
          They are shown in their own columns above and in the CSV, and are
          counted in Total Check-ins — they are never guessed into self-service,
          operator-assisted or CTA.
        </p>
      )}
    </div>
  );
};

export default EventPerformance;
